import {
  DISCOUNT_TYPE,
  PAYMENT_STATUS,
  RECEIPT_PAYMENT_MODE_ID,
  RECEIPT_STATUS_TYPE,
  RECEIPT_TYPE,
} from "../enums/receipt";
import { Appointment } from "./appointment";

export type TReceiptDiscountTypeIds = "PERCENTAGE" | "AMOUNT";

export type TReceiptPayment = {
  amount: number;
  paymode: RECEIPT_PAYMENT_MODE_ID;
};

export type TReceipt = {
  additional_discount_amount: number;
  additional_discount_type: DISCOUNT_TYPE;
  additional_discount_value: number;
  mobile: string;
  name: string;
  remarks: string;
  net_amount: number;
  receipt_amount: number;
  receipt_id: number;
  receipt_number?: string;
  business_receipt_number?: string;
  receipt_url: string | null;
  receipt_payment: Array<TReceiptPayment>;
  receipt_sku: Array<TReceiptSku>;
  updated_at: string | null;
  payment_status: PAYMENT_STATUS;
  receipt_type: RECEIPT_TYPE;
  amount_due: number;
  payment_id: string;
  ref_trx_id?: string;
};

export type TReceiptSku = {
  net_amount: number;
  amount: number;
  discount_amount: number;
  discount_type: DISCOUNT_TYPE;
  discount_value: number;
  quantity: number;
  sku_item: TSkuItem;
};

export type TSkuItem = {
  service_name: string;
  service_id: number;
  price: number;
  archive: boolean;
};

export type TCreateReceiptBody = {
  payload: Pick<
    TReceipt,
    | "name"
    | "mobile"
    | "receipt_amount"
    | "receipt_number"
    | "additional_discount_value"
    | "additional_discount_type"
    | "additional_discount_amount"
    | "net_amount"
    | "remarks"
    | "business_receipt_number"
  > & {
    receipt_payments: Array<TReceiptPayment>;
    receipt_skus: (Omit<TReceiptSku, "sku_item"> &
      Omit<TSelectedSkuItem, "archive">)[];
    amount_due?: number;
    ref_transaction_id?: string;
  };
  pdf_url?: string;
  meta: {
    doctor_oid?: string;
    clinic_id: string;
    aid: string | null;
    patient_oid: string;
    request_payment?: boolean;
    receipt_type?: RECEIPT_TYPE;
  };
};

export type TCreateReceiptResponse = TReceipt & {
  aid: string;
  doctor_oid: string;
  created_at: Date;
  receipt_status: RECEIPT_STATUS_TYPE;
  pdfConfigForGeneration?: TGetReceiptPdfConfig;
  shouldSendMessage?: boolean;
  Appointment: {
    date: string;
    patient_oid: string;
    patient_uuid: string;
    full_date: number; // epoch
  };
  receiptAmountinWords?: string;
  meta: {
    pdfConfigForGeneration: TGetReceiptPdfConfig;
    headerText: string;
  };
  patientData: {
    oid: string;
    uhid: string;
    dob?: string;
    gender: string;
    age: number | undefined;
    fn: string;
    ln: string;
    mobile: string;
    extras?: {
      formData?: Array<{
        key: string;
        value: string;
        label: string;
        type: string;
      }>;
    };
  };
  doctorProfile: {
    name: string;
    headerText: string;
    clinicObject: any;
  };
  commEventName?: string;
  headerText: string;
  sku_discount_amount: number;
};

export type TPageSize = "A4" | "A5";

export enum DateAndTimeConfigForReceipt {
  DATE_ONLY = "date_only", // default
  DATE_AND_TIME = "date_and_time",
  EXTENDED_DATE_AND_TIME = "extended_date_and_time",
}

export interface TGetReceiptPdfConfig {
  doctor_oid?: string;
  clinic_id: string;
  page_size: TPageSize;
  margins: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  heights: {
    header: number;
    footer: number;
  };
  flags: {
    print_header: boolean;
    print_footer: boolean;
    print_uhid: boolean;
    print_receipt_number: boolean;
    discount_column: "ALWAYS" | "IF_APL";
    quantity_column: "ALWAYS" | "IF_APL";
    print_paymode_split: boolean;
    print_transaction_id: boolean;
    print_amount_in_words?: boolean;
    date_and_time?: DateAndTimeConfigForReceipt;
    bill_created_at?: boolean;
    print_bill_created_by?: boolean;
  };
  header_image_url?: string;
  footer_image_url?: string;
  constraints?: {
    require_uhid: boolean;
  };
}

export type TPostReceiptConfigRequestPayload = {
  receipt_pdf_header?: File;
  receipt_pdf_footer?: File;
  clinic_id: string;
  doctor_oid: string;
} & Partial<TGetReceiptPdfConfig>;

export type TBillingQueueRequestObject = {
  clinicId: string;
  startDate: string;
  endDate?: string;
};

export type TReceiptCounter = {
  receipt_prefix: string;
  receipt_counter: number;
  clinic_id: string;
};

export interface Cost {
  curr: string;
  price: number;
  qty?: number;
  discount?: number;
  priceToShowWithType?: string;
}

export type DiscountType = {
  id: DISCOUNT_TYPE;
  name: string;
};

export type LocalServiceEntity = {
  id: number | null;
  name: string;
  cost: Cost & { discountType: DiscountType; discountToShowWithType: string };
};

// export type TService = {
//   service_id: string;
//   service_name: string;
//   price: number;
// };

export type TStorePaymode = {
  paymode: RECEIPT_PAYMENT_MODE_ID;
  amount: number;
  amountToShowWithType?: string;
};
export type TSelectedSkuItem = Omit<TSkuItem, "service_id" | "archive"> & {
  service_id: number | null;
  archive?: boolean;
};

export type TReceiptEntity = {
  idx: number;
  doctor_oid: string;
  aid: string;
  paymentItems: Array<TReceiptPayment>;
  receiptSkus: Array<TReceiptSku>;
  patient: Appointment["patient"];
  clinic_id: string;
  uhid: string | null;
  receipt_number?: string;
  business_receipt_number?: string;
  remarks: string | null;
  receipt_id: number | null;
  receipt_url: string | null;
  transaction_date: Date | null;
  visited_on: Date | null;
  tags: Appointment["tags"];
  receipt_amount: string;
  net_amount: string;
  amount_due: number;
  payment_status: TReceipt["payment_status"];
  total_discount_amount: number;
  additional_discount_amount: number;
  receipt_type:
    | "INCLINIC"
    | "EKAPREPAID"
    | "REFUND"
    | "DUE_PAYMENT"
    | "CREDIT_NOTE";
};
